const textEncoder = new TextEncoder();

/**
 * Builds SSH binary payloads by appending typed fields.
 *
 * Usage:
 *   const w = new SshMessageWriter();
 *   w.writeByte(SSH_MSG_KEXINIT);
 *   w.writeString(payload);
 *   const bytes = w.toUint8Array();
 */
export class SshMessageWriter {
	private parts: Uint8Array[] = [];
	private length = 0;

	/** Write a single byte (0-255). */
	writeByte(value: number): this {
		const buf = new Uint8Array(1);
		buf[0] = value & 0xff;
		this.parts.push(buf);
		this.length += 1;
		return this;
	}

	/** Write a boolean (1 byte: 0 or 1). */
	writeBoolean(value: boolean): this {
		return this.writeByte(value ? 1 : 0);
	}

	/** Write a uint32 in big-endian. */
	writeUint32(value: number): this {
		const buf = new Uint8Array(4);
		new DataView(buf.buffer).setUint32(0, value >>> 0);
		this.parts.push(buf);
		this.length += 4;
		return this;
	}

	/** Write a uint64 in big-endian from a bigint. */
	writeUint64(value: bigint): this {
		const buf = new Uint8Array(8);
		new DataView(buf.buffer).setBigUint64(0, value);
		this.parts.push(buf);
		this.length += 8;
		return this;
	}

	/** Write raw bytes without a length prefix. */
	writeRawBytes(data: Uint8Array): this {
		this.parts.push(data);
		this.length += data.length;
		return this;
	}

	/**
	 * Write an SSH "string" (uint32 length + bytes).
	 * Accepts either raw bytes or a UTF-8 string.
	 */
	writeString(data: Uint8Array | string): this {
		const bytes = typeof data === 'string' ? textEncoder.encode(data) : data;
		this.writeUint32(bytes.length);
		this.parts.push(bytes);
		this.length += bytes.length;
		return this;
	}

	/**
	 * Write an SSH "mpint" (multiple precision integer).
	 * Encodes a bigint as a two's complement big-endian byte string,
	 * with minimal encoding (no unnecessary leading zeros/FFs).
	 */
	writeMpint(value: bigint): this {
		if (value === 0n) {
			// zero is encoded as an empty string
			return this.writeUint32(0);
		}

		const negative = value < 0n;
		const abs = negative ? -value : value;

		// convert to big-endian bytes
		const hexStr = abs.toString(16);
		const padded = hexStr.length % 2 === 0 ? hexStr : '0' + hexStr;
		const bytes: number[] = [];
		for (let i = 0; i < padded.length; i += 2) {
			bytes.push(parseInt(padded.substring(i, i + 2), 16));
		}

		if (negative) {
			// two's complement: invert and add 1
			for (let i = 0; i < bytes.length; i++) {
				bytes[i] = ~bytes[i]! & 0xff;
			}
			// add 1
			let carry = 1;
			for (let i = bytes.length - 1; i >= 0 && carry; i--) {
				const sum = bytes[i]! + carry;
				bytes[i] = sum & 0xff;
				carry = sum >> 8;
			}
			// ensure high bit is set (negative)
			if (bytes.length > 0 && !(bytes[0]! & 0x80)) {
				bytes.unshift(0xff);
			}
		} else {
			// ensure high bit is clear (positive)
			if (bytes.length > 0 && bytes[0]! & 0x80) {
				bytes.unshift(0x00);
			}
		}

		const buf = new Uint8Array(bytes);
		this.writeUint32(buf.length);
		this.parts.push(buf);
		this.length += buf.length;
		return this;
	}

	/**
	 * Write an SSH "name-list" (comma-separated ASCII names, length-prefixed).
	 */
	writeNameList(names: readonly string[]): this {
		return this.writeString(names.join(','));
	}

	/** Returns the total byte length written so far. */
	get byteLength(): number {
		return this.length;
	}

	/** Concatenate all parts into a single Uint8Array. */
	toUint8Array(): Uint8Array {
		const result = new Uint8Array(this.length);
		let offset = 0;
		for (const part of this.parts) {
			result.set(part, offset);
			offset += part.length;
		}
		return result;
	}
}
